import React, { useEffect, useRef, useState } from "react";
import Button from "./Button";

const clampIndex = (value, count, loop) => {
  if (count <= 0) return 0;
  if (!loop) {
    return Math.min(Math.max(0, value), count - 1);
  }
  return ((value % count) + count) % count;
};

export const useCarousel = ({
  count = 0,
  current = 0,
  onChange,
  loop = true,
  autoPlay = false,
  intervalMs = 15000,
} = {}) => {
  const isControlled = typeof onChange === "function";
  const [internal, setInternal] = useState(0);
  const [paused, setPaused] = useState(false);
  const [tick, setTick] = useState(0);
  const timerRef = useRef(null);

  const index = clampIndex(isControlled ? current : internal, count, loop);

  const go = (value) => {
    if (count <= 0) return;
    const next = clampIndex(value, count, loop);
    if (isControlled) {
      onChange?.(next);
    } else {
      setInternal(next);
    }
    setTick((value2) => value2 + 1);
  };

  const next = () => go(index + 1);
  const prev = () => go(index - 1);
  const pause = () => setPaused(true);
  const resume = () => setPaused(false);

  useEffect(() => {
    if (!isControlled) {
      setInternal((prevValue) => clampIndex(prevValue, count, loop));
    }
  }, [count, isControlled, loop]);

  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    if (!autoPlay || paused || count <= 1) return undefined;

    setTick((value) => value + 1);
    timerRef.current = setTimeout(() => {
      if (isControlled) {
        onChange?.(clampIndex(index + 1, count, loop));
      } else {
        setInternal((value) => clampIndex(value + 1, count, loop));
      }
    }, intervalMs);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [autoPlay, paused, count, intervalMs, index, isControlled, onChange, loop]);

  return {
    index,
    go,
    next,
    prev,
    isControlled,
    paused,
    pause,
    resume,
    tick,
  };
};

const createSlide = (index, activeIndex) => ({
  className: index === activeIndex ? "is-active" : "",
});

export default function Carousel({
  items = [],
  current = 0,
  onChange,
  className = "",
  ariaLabel = "Carousel",
  renderItem,
  initialIndex = 0,
}) {
  const count = Array.isArray(items) ? items.length : 0;
  const listRef = useRef(null);
  const [metrics, setMetrics] = useState({
    containerWidth: 0,
    slideWidth: 0,
    gap: 0,
    height: 0,
  });

  const carousel = useCarousel({
    count,
    current,
    onChange,
    loop: false,
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!listRef.current) return;

    const compute = () => {
      const list = listRef.current;
      if (!list) return;
      const first = list.querySelector(".rm-carousel__slide-item");
      if (!first) return;
      const rect = first.getBoundingClientRect();
      const slideWidth = Math.round(rect.width || first.offsetWidth || 0);
      const height = Math.round(rect.height || first.offsetHeight || 0);
      const listStyle = window.getComputedStyle(list);
      const gapValue = listStyle.columnGap || listStyle.gap || "0px";
      const gap = Math.round(parseFloat(gapValue) || 0);
      const containerWidth = Math.round(list.parentElement?.clientWidth || 0);
      setMetrics({
        containerWidth,
        slideWidth,
        gap,
        height,
      });
    };

    compute();
    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(compute);
      observer.observe(listRef.current);
      return () => observer.disconnect();
    }
  }, [items]);

  const step = metrics.slideWidth + metrics.gap;
  const isMobile = metrics.containerWidth > 0 && metrics.containerWidth <= 768;
  const edgePad =
    isMobile && metrics.containerWidth > 0 && metrics.slideWidth > 0
      ? Math.max(0, (metrics.containerWidth - metrics.slideWidth) / 2)
      : 0;
  const perPage =
    metrics.containerWidth > 0 && metrics.slideWidth > 0
      ? Math.max(
          1,
          Math.floor(
            metrics.containerWidth / (metrics.slideWidth + metrics.gap),
          ),
        )
      : 1;
  const listWidth =
    metrics.slideWidth > 0
      ? metrics.slideWidth * items.length +
        metrics.gap * Math.max(0, items.length - 1) +
        edgePad * 2
      : 0;
  const maxTranslate = Math.max(0, listWidth - metrics.containerWidth);
  const desiredTranslate = step * carousel.index;
  const clampedTranslate = Math.max(0, Math.min(desiredTranslate, maxTranslate));
  const navStep = isMobile ? 1 : perPage;
  const pageIndex = navStep > 0 ? Math.floor(carousel.index / navStep) : 0;
  const lastStartIndex = Math.max(0, count - navStep);
  const pageToIndex = (page) =>
    Math.min(Math.max(page * navStep, 0), lastStartIndex);

  const handlePrev = () => {
    if (count <= 0) return;
    carousel.go(pageToIndex(pageIndex - 1));
  };

  const handleNext = () => {
    if (count <= 0) return;
    carousel.go(pageToIndex(pageIndex + 1));
  };

  const startSwipe = (target, x, y) => {
    if (!target?.dataset) return;
    target.dataset.swipeActive = "1";
    target.dataset.swipeStartX = String(x);
    target.dataset.swipeStartY = String(y);
  };

  const endSwipe = (target, x, y) => {
    if (!target?.dataset) return;
    if (target.dataset.swipeActive !== "1") return;
    target.dataset.swipeActive = "0";
    const startX = Number(target.dataset.swipeStartX || 0);
    const startY = Number(target.dataset.swipeStartY || 0);

    const deltaX = x - startX;
    const deltaY = y - startY;
    const minSwipe = 36;

    // Only treat mostly-horizontal motion as carousel navigation.
    if (Math.abs(deltaX) < minSwipe || Math.abs(deltaX) <= Math.abs(deltaY)) {
      return;
    }

    if (deltaX < 0) {
      handleNext();
    } else {
      handlePrev();
    }
  };

  return (
    <div
      className={["rm-carousel", className].filter(Boolean).join(" ")}
      aria-roledescription="carousel"
      aria-label={ariaLabel}
    >
      <div className="rm-carousel__wrap">
        <Button
          className="rm-carousel__btn rm-carousel__btn--prev"
          icon="chevron_left"
          iconOnly
          aria-label="Previous"
          onClick={handlePrev}
        />
        <div className="rm-carousel__container">
          <ul
            className="rm-carousel__slide-list"
            ref={listRef}
            onTouchStart={(event) => {
              const touch = event.touches?.[0];
              if (!touch) return;
              startSwipe(event.currentTarget, touch.clientX, touch.clientY);
            }}
            onTouchEnd={(event) => {
              const touch = event.changedTouches?.[0];
              if (!touch) return;
              endSwipe(event.currentTarget, touch.clientX, touch.clientY);
            }}
            onPointerDown={(event) => {
              if (event.pointerType === "mouse" && event.button !== 0) return;
              startSwipe(event.currentTarget, event.clientX, event.clientY);
            }}
            onPointerUp={(event) => {
              endSwipe(event.currentTarget, event.clientX, event.clientY);
            }}
            onPointerCancel={(event) => {
              if (!event.currentTarget?.dataset) return;
              event.currentTarget.dataset.swipeActive = "0";
            }}
            style={{
              transform:
                metrics.slideWidth > 0
                  ? `translateX(-${clampedTranslate}px)`
                  : undefined,
              minHeight: metrics.height > 0 ? `${metrics.height}px` : undefined,
              "--rm-carousel-edge-pad": `${edgePad}px`,
            }}
          >
            {items.map((item, idx) => {
              const { className: stateClass } = createSlide(
                idx,
                carousel.index,
              );
              return (
                <li
                  key={`slide-${idx}-${item?.id ?? "item"}`}
                  className={["rm-carousel__slide-item", stateClass]
                    .filter(Boolean)
                    .join(" ")}
                >
                  {renderItem ? renderItem(item, idx) : null}
                </li>
              );
            })}
          </ul>
        </div>
        <Button
          className="rm-carousel__btn rm-carousel__btn--next"
          icon="chevron_right"
          iconOnly
          aria-label="Next"
          onClick={handleNext}
        />
      </div>
      <div
        className="rm-carousel__dots"
        role="tablist"
        aria-label="Carousel items"
      >
        {items.map((item, idx) => (
          <button
            key={`dot-${item?.id ?? idx}`}
            type="button"
            role="tab"
            aria-selected={carousel.index === idx}
            aria-label={`Go to item ${idx + 1}`}
            className={[
              "rm-carousel__dot",
              carousel.index === idx ? "is-active" : "",
            ]
              .filter(Boolean)
              .join(" ")}
            onClick={() => carousel.go(idx)}
          />
        ))}
      </div>
    </div>
  );
}
